import { Rule } from 'antd/es/form'
import { ConsentStatus } from 'fhir/Consent/constants'
import { Diagnosis, PatientMedication } from 'components/PatientChart/types'
import { MedicationUsageStatus } from 'components/Common/Medication/types'
import { StateEnum } from 'constants/states'
import { EpisodeOfCareStatusEnum } from 'fhir/EpisodeofCare/constants'
import { CareTeamMemberRole } from 'fhir/CareTeam/constants'
import { PatientChartFormState } from './types'

export enum PatientChartSections {
  Demographics = 'demographics',
  CareLocation = 'care-location',
  InsuranceInformation = 'insurance-information',
  MedicalBackground = 'medical-background',
  CareTeam = 'care-team',
  ServiceInformation = 'service-information',
  CertificationPeriods = 'certification-periods',
  AcutePeriods = 'acute-periods',
  CarePeriods = 'care-periods'
}

export const careTeamContactRoles = [
  CareTeamMemberRole.AlternateCaregiver,
  CareTeamMemberRole.EmergencyContact
]

export type FormInputRow = {
  label: string
  name?: string
  placeholder?: string
  input?: JSX.Element
  disabled?: boolean
  optional?: boolean
  rules?: Rule[]
  customFormItem?: JSX.Element // Allows for custom displays (e.g. inline state and postal code)
  extra?: string
}

export const consentStatusMap = {
  [ConsentStatus.ACTIVE]: {
    text: 'Active',
    style: {
      bg: '#12B76A',
      text: 'white'
    }
  },
  [ConsentStatus.INACTIVE]: {
    text: 'Inactive',
    style: {
      bg: '#F9A825',
      text: 'white'
    }
  },
  [ConsentStatus.PROPOSED]: {
    text: 'Proposed',
    style: {
      bg: '#F9A825',
      text: 'white'
    }
  },
  [ConsentStatus.REJECTED]: {
    text: 'Rejected',
    style: {
      bg: '#FF0000',
      text: 'white'
    }
  },
  [ConsentStatus.ENTERED_IN_ERROR]: {
    text: 'Entered in Error',
    style: {
      bg: '#FF0000',
      text: 'white'
    }
  }
}

export const episodeOfCareStatusMap = {
  [EpisodeOfCareStatusEnum.ACTIVE]: {
    text: 'Active',
    style: {
      bg: '#12B76A',
      text: 'white'
    }
  },
  [EpisodeOfCareStatusEnum.PLANNED]: {
    text: 'Planned',
    style: {
      bg: '#F9A825',
      text: 'white'
    }
  },
  [EpisodeOfCareStatusEnum.WAITLIST]: {
    text: 'Waitlist',
    style: {
      bg: '#FF0000',
      text: 'white'
    }
  },
  [EpisodeOfCareStatusEnum.ON_HOLD]: {
    text: 'On Hold',
    style: {
      bg: '#FF0000',
      text: 'white'
    }
  },
  [EpisodeOfCareStatusEnum.FINISHED]: {
    text: 'Finished',
    style: {
      bg: '#FF0000',
      text: 'white'
    }
  },
  [EpisodeOfCareStatusEnum.CANCELLED]: {
    text: 'Cancelled',
    style: {
      bg: '#FF0000',
      text: 'white'
    }
  },
  [EpisodeOfCareStatusEnum.ENTERED_IN_ERROR]: {
    text: 'Entered in Error',
    style: {
      bg: '#FF0000',
      text: 'white'
    }
  }
}

const DEFAULT_RATING = '0'
const DEFAULT_DIAGNOSIS = 'Cholera due to Vibrio cholerae 01, biovar cholerae'
const DEFAULT_DIAGNOSIS_CODE = 'A000'

export const PLACEHOLDER_DIAGNOSIS: Diagnosis = {
  name: DEFAULT_DIAGNOSIS,
  code: DEFAULT_DIAGNOSIS_CODE,
  rating: DEFAULT_RATING,
  startDate: null,
  endDate: null
}

export const DEFAULT_PATIENT_CHART_FORM_STATE: PatientChartFormState = {
  demographics: {
    firstName: '',
    preferredName: '',
    lastName: '',
    dateOfBirth: '',
    activationStatus: false,
    admissionStatus: '',
    sex: '',
    gender: '',
    ethnicity: '',
    primaryLanguage: '',
    ssn: '',
    serviceTypes: [],
    socDate: '',
    f2fDate: '',
    mrn: '',
    agency: '',
    address: {
      streetLine1: '',
      streetLine2: '',
      city: '',
      state: StateEnum.NO_STATE_SELECTED,
      postalCode: ''
    }
  },
  serviceInformation: [],
  careTeam: [],
  medicalBackground: {
    primaryDiagnosis: undefined,
    otherDiagnoses: [],
    medications: [],
    primaryCareOrganizationId: ''
  },
  primaryCareLocation: {},
  secondaryCareLocation: {},
  insuranceInformation: {}
}

export const DEFAULT_PATIENT_MEDICATION: PatientMedication = {
  name: '',
  dosage: {
    value: 0,
    unit: ''
  },
  strength: {
    value: 0,
    unit: ''
  },
  route: '',
  frequency: '',
  startDate: null,
  endDate: null,
  usageStatus: MedicationUsageStatus.NEW,
  instructions: '',
  indications: '',
  isControlled: false,
  isLiquid: false,
  isInfectionTreatment: false,
  requesterId: '',
  drugbankId: ''
}

export const inHomeNurseSafetyRisksOptions = [
  { value: 'pets_dogs_cats', label: 'Pets (dogs, cats)' },
  { value: 'aggressive_pets', label: 'Aggressive pets' },
  { value: 'allergen_exposure', label: 'Allergen exposure' },
  { value: 'crime_rate_in_neighborhood', label: 'Crime rate in neighborhood' },
  { value: 'unsanitary_home_conditions', label: 'Unsanitary home conditions' },
  { value: 'poor_lighting', label: 'Poor lighting' },
  { value: 'stairs_and_uneven_surfaces', label: 'Stairs and uneven surfaces' },
  { value: 'patient_or_family_aggression', label: 'Patient or family aggression' },
  { value: 'weapons_in_the_home', label: 'Weapons in the home' },
  { value: 'poor_ventilation', label: 'Poor ventilation' },
  { value: 'unsafe_electrical_setup', label: 'Unsafe electrical setup' },
  { value: 'no_intermittent_phone_service', label: 'No intermittent phone service' },
  { value: 'smoking_household', label: 'Smoking household' },
  { value: 'blocked_exits', label: 'Blocked exits' },
  { value: 'other', label: 'Other' },
  { value: 'no_risks', label: 'No risks' }
]

export const patientChartAnchors = [
  {
    key: PatientChartSections.Demographics,
    href: `#${PatientChartSections.Demographics}`,
    label: 'Demographics'
  },
  {
    key: PatientChartSections.CareLocation,
    href: `#${PatientChartSections.CareLocation}`,
    label: 'Care Locations'
  },
  {
    key: PatientChartSections.InsuranceInformation,
    href: `#${PatientChartSections.InsuranceInformation}`,
    label: 'Insurance Information'
  },
  {
    key: PatientChartSections.MedicalBackground,
    href: `#${PatientChartSections.MedicalBackground}`,
    label: 'Medical Background'
  },
  {
    key: PatientChartSections.CareTeam,
    href: `#${PatientChartSections.CareTeam}`,
    label: 'Care Team'
  },
  {
    key: PatientChartSections.ServiceInformation,
    href: `#${PatientChartSections.ServiceInformation}`,
    label: 'Service Information'
  },
  {
    key: PatientChartSections.CertificationPeriods,
    href: `#${PatientChartSections.CertificationPeriods}`,
    label: 'Certification Periods'
  },
  {
    key: PatientChartSections.AcutePeriods,
    href: `#${PatientChartSections.AcutePeriods}`,
    label: 'Acute Periods'
  },
  {
    key: PatientChartSections.CarePeriods,
    href: `#${PatientChartSections.CarePeriods}`,
    label: 'Care Periods'
  }
]

export enum AdmissionStatus {
  INTAKE = 'intake',
  ON_HOLD = 'on-hold',
  NON_ADMIT = 'non-admit',
  DISCHARGED = 'discharged',
  ADMITTED = 'admitted',
  ACUTE = 'acute',
  HOSPITALIZED = 'hospitalized',
  PRE_ADMISSION = 'pre-admission'
}

export const ADMISSION_STATUS_OPTIONS = [
  { value: AdmissionStatus.INTAKE, label: 'Intake' },
  { value: AdmissionStatus.ON_HOLD, label: 'On Hold' },
  { value: AdmissionStatus.NON_ADMIT, label: 'Non Admit' },
  { value: AdmissionStatus.DISCHARGED, label: 'Discharged' },
  { value: AdmissionStatus.ADMITTED, label: 'Admitted' },
  { value: AdmissionStatus.ACUTE, label: 'Acute' },
  { value: AdmissionStatus.HOSPITALIZED, label: 'Hospitalized' },
  { value: AdmissionStatus.PRE_ADMISSION, label: 'Pre-Admission' }
]
